import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import toast from 'react-hot-toast';

//const SUBS_URL = 'http://localhost:5002/api';
const SUBS_URL = process.env.REACT_APP_SUBS_URL || 'http://localhost:5002/api';

const OtpModal = ({ email, onVerified, onClose, pendingData }: {
    email: string;
    onVerified: (token: string, user: any) => void;
    onClose: () => void;
    pendingData: { fullName: string; password: string };
}) => {
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [secondsLeft, setSecondsLeft] = useState(60);
    const [resending, setResending] = useState(false);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const startTimer = () => {
        if (timerRef.current) clearInterval(timerRef.current);
        setSecondsLeft(60);
        timerRef.current = setInterval(() => {
            setSecondsLeft(s => {
                if (s <= 1) { clearInterval(timerRef.current!); return 0; }
                return s - 1;
            });
        }, 1000);
    };

    useState(() => {
        setTimeout(() => inputRefs.current[0]?.focus(), 100);
        startTimer();
        return () => { if (timerRef.current) clearInterval(timerRef.current); };
    });

    const handleChange = (index: number, val: string) => {
        if (!/^\d*$/.test(val)) return;
        const next = [...otp];
        next[index] = val.slice(-1);
        setOtp(next);
        setError('');
        if (val && index < 5) inputRefs.current[index + 1]?.focus();
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) inputRefs.current[index - 1]?.focus();
        if (e.key === 'Enter') handleVerify();
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        if (pasted.length === 6) { setOtp(pasted.split('')); inputRefs.current[5]?.focus(); }
    };

    const handleVerify = async () => {
        const code = otp.join('');
        if (code.length !== 6) { setError('Enter the 6-digit OTP'); return; }
        setLoading(true);
        try {
            const res = await fetch(`${SUBS_URL}/auth/verify-register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, otpCode: code, fullName: pendingData.fullName, password: pendingData.password })
            });
            const data = await res.json();
            if (data.success) onVerified(data.token, data.user);
            else setError(data.message || 'Invalid OTP');
        } catch { setError('Server error. Try again.'); }
        finally { setLoading(false); }
    };

    const handleResend = async () => {
        setResending(true);
        try {
            const res = await fetch(`${SUBS_URL}/auth/send-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, fullName: pendingData.fullName, password: pendingData.password })
            });
            const data = await res.json();
            if (data.success) { setOtp(['', '', '', '', '', '']); setError(''); startTimer(); toast.success('New OTP sent!'); setTimeout(() => inputRefs.current[0]?.focus(), 50); }
            else toast.error(data.message);
        } catch { toast.error('Server error'); }
        finally { setResending(false); }
    };

    const mins = Math.floor(secondsLeft / 60);
    const secs = secondsLeft % 60;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-7">
                <div className="text-center mb-6">
                    <div className="w-14 h-14 bg-teal-50 border border-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="text-teal-600" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                            <polyline points="22,6 12,13 2,6"/>
                        </svg>
                    </div>
                    <h2 className="text-lg font-bold text-gray-900">Verify Your Email</h2>
                    <p className="text-sm text-gray-500 mt-1">6-digit code sent to<br/><span className="font-semibold text-gray-800">{email}</span></p>
                </div>
                <div className="flex gap-2 justify-center mb-5" onPaste={handlePaste}>
                    {otp.map((digit, i) => (
                        <input key={i} ref={el => { inputRefs.current[i] = el; }}
                            type="text" inputMode="numeric" maxLength={1} value={digit}
                            onChange={e => handleChange(i, e.target.value)}
                            onKeyDown={e => handleKeyDown(i, e)}
                            className={`w-11 h-12 text-center text-lg font-bold border-2 rounded-xl focus:outline-none transition-all ${digit ? 'border-teal-500 bg-teal-50 text-teal-700' : error ? 'border-red-300' : 'border-gray-200 focus:border-teal-400'}`}
                        />
                    ))}
                </div>
                {error && <p className="text-red-500 text-xs text-center mb-4">{error}</p>}
                <div className="text-center mb-5">
                    {secondsLeft > 0 ? (
                        <p className="text-sm text-gray-400">Expires in{' '}<span className={`font-bold font-mono ${secondsLeft <= 10 ? 'text-red-500' : 'text-teal-600'}`}>{String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}</span></p>
                    ) : <p className="text-sm text-red-500 font-medium">OTP expired</p>}
                </div>
                <button onClick={handleVerify} disabled={loading || otp.join('').length !== 6}
                    className="w-full py-3 bg-teal-600 hover:bg-teal-700 text-white text-sm font-bold rounded-xl transition-all disabled:opacity-60 flex items-center justify-center gap-2 mb-3">
                    {loading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>}
                    Verify & Create Account
                </button>
                <div className="flex items-center justify-between text-sm">
                    <button onClick={handleResend} disabled={secondsLeft > 0 || resending}
                        className="text-teal-600 hover:underline font-semibold disabled:opacity-40 disabled:no-underline">
                        {resending ? 'Sending...' : 'Resend OTP'}
                    </button>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">Cancel</button>
                </div>
            </div>
        </div>
    );
};

// ✅ Shared: save token + hard reload
const saveAndRedirect = (token: string, user: any) => {
    localStorage.setItem('sub_token', token);
    localStorage.setItem('sub_user', JSON.stringify(user));
    window.location.href = '/'; // ✅ Full reload — AuthContext fresh initialize hoga
};

const RegisterPage = () => {
    const emailRef = useRef<HTMLInputElement>(null);
    const passwordRef = useRef<HTMLInputElement>(null);
    const [form, setForm] = useState({ fullName: '', email: '', password: '' });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [showOtp, setShowOtp] = useState(false);

    const validate = () => {
        const e: Record<string, string> = {};
        if (!form.fullName.trim()) e.fullName = 'Full name is required';
        if (!form.email.trim()) e.email = 'Email is required';
        else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Invalid email';
        if (!form.password) e.password = 'Password is required';
        else if (form.password.length < 6) e.password = 'Minimum 6 characters';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setLoading(true);
        try {
            const res = await fetch(`${SUBS_URL}/auth/send-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ fullName: form.fullName.trim(), email: form.email.trim(), password: form.password })
            });
            const data = await res.json();
            if (data.success) setShowOtp(true);
            else setErrors({ general: data.message || 'Failed to send OTP' });
        } catch { setErrors({ general: 'Server error. Try again.' }); }
        finally { setLoading(false); }
    };

    const handleGoogleSuccess = async (credentialResponse: any) => {
        try {
            const res = await fetch(`${SUBS_URL}/auth/google`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ idToken: credentialResponse.credential })
            });
            const data = await res.json();
            if (data.success) {
                toast.success('Welcome! 🎉');
                saveAndRedirect(data.token, data.user);
            } else toast.error(data.message || 'Google sign-in failed');
        } catch { toast.error('Google sign-in failed'); }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <Link to="/" className="inline-flex items-center gap-2 mb-5">
                        <div className="w-9 h-9 bg-teal-600 rounded-xl flex items-center justify-center shadow-sm">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
                                <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
                            </svg>
                        </div>
                        <span className="text-xl font-bold text-gray-800">Med<span className="text-teal-600">Code</span><span className="text-gray-400 font-medium">Lookup</span></span>
                    </Link>
                    <h1 className="text-2xl font-bold text-gray-900">Create your account</h1>
                    <p className="text-sm text-gray-500 mt-1">Start with <strong>2 free searches</strong> per day</p>
                </div>

                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8">
                    <div className="space-y-5">
                        {/* Full Name */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Full Name</label>
                            <input type="text" autoFocus value={form.fullName}
                                onChange={e => setForm(f => ({ ...f, fullName: e.target.value }))}
                                onKeyDown={e => { if (e.key === 'Tab') { e.preventDefault(); emailRef.current?.focus(); } }}
                                placeholder="John Doe"
                                className={`w-full px-4 py-3 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-400 transition-all text-gray-800 placeholder-gray-400 ${errors.fullName ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50 focus:bg-white'}`}
                            />
                            {errors.fullName && <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>}
                        </div>
                        {/* Email */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email Address</label>
                            <input ref={emailRef} type="email" value={form.email}
                                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                                onKeyDown={e => { if (e.key === 'Tab') { e.preventDefault(); passwordRef.current?.focus(); } }}
                                placeholder="you@example.com"
                                className={`w-full px-4 py-3 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-400 transition-all text-gray-800 placeholder-gray-400 ${errors.email ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50 focus:bg-white'}`}
                            />
                            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                        </div>
                        {/* Password */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Password</label>
                            <input ref={passwordRef} type="password" value={form.password}
                                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                                onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }}
                                placeholder="Minimum 6 characters"
                                className={`w-full px-4 py-3 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-400 transition-all text-gray-800 placeholder-gray-400 ${errors.password ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50 focus:bg-white'}`}
                            />
                            {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
                        </div>

                        {errors.general && (
                            <div className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                                <svg className="text-red-400 flex-shrink-0" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                                <p className="text-red-600 text-xs font-medium">{errors.general}</p>
                            </div>
                        )}

                        {/* Submit */}
                        <button onClick={handleSubmit} disabled={loading}
                            className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold py-3 rounded-xl transition-all disabled:opacity-60 flex items-center justify-center gap-2 text-sm">
                            {loading ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/> Sending OTP...</> : 'Continue — Verify Email'}
                        </button>

                        {/* Divider */}
                        <div className="flex items-center gap-3">
                            <div className="flex-1 h-px bg-gray-100"/>
                            <span className="text-xs text-gray-400">or</span>
                            <div className="flex-1 h-px bg-gray-100"/>
                        </div>

                        {/* ✅ Google Button — form ke baad, Sign In se pehle */}
                        <div className="flex justify-center">
                            <GoogleLogin
                                onSuccess={handleGoogleSuccess}
                                onError={() => toast.error('Google sign-in failed')}
                                text="continue_with"
                                shape="rectangular"
                                theme="outline"
                                size="large"
                                width="368"
                            />
                        </div>
                    </div>

                    <div className="flex items-center gap-3 my-5">
                        <div className="flex-1 h-px bg-gray-100"/>
                        <span className="text-xs text-gray-400">already have an account?</span>
                        <div className="flex-1 h-px bg-gray-100"/>
                    </div>

                    {/* Sign In — sab se neche */}
                    <Link to="/login" className="block w-full text-center py-3 border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:border-teal-300 transition-all">
                        Sign In
                    </Link>
                </div>
                <p className="text-center text-xs text-gray-400 mt-4">🔒 Secure & encrypted. No spam ever.</p>
            </div>

            {showOtp && (
                <OtpModal
                    email={form.email}
                    pendingData={{ fullName: form.fullName, password: form.password }}
                    onVerified={(token, user) => { toast.success('Account created! Welcome 🎉'); saveAndRedirect(token, user); }}
                    onClose={() => setShowOtp(false)}
                />
            )}
        </div>
    );
};

export default RegisterPage;